import { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  Divider,
  Tabs,
  Row,
  Col,
  Skeleton,
  Select,
} from "antd";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { map, get } from "lodash";
import moment from "moment";

import service from "../../services/nysService";

import {
  CheckOutlined,
  CloseOutlined,
  FundViewOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { recordError } from "../../utils/logService";
import { openNotification } from "@seims/shared";
import { Notify } from "../../services/custom/Messages";

const CandidatesList = ({ hasUpdated = false, statuses, defaultStatus }) => {
  const { Option } = Select;
  const [candidates, setCandidates] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [finYearIntakes, setFinYearIntake] = useState([]);
  const [selectedIntake, setSelectedIntake] = useState(undefined);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState();
  const [tableAction, setTableAction] = useState("Manage");
  let submittedStatus = defaultStatus.key;
  const [activeKey, setActiveKey] = useState(submittedStatus);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [firstRecord, setFirstRecord] = useState(0);
  const [isNotSearchable, setIsNotSearchable] = useState(true);
  const [isSearchedResults, setIsSearchedResults] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  let msg = "";

  useEffect(() => {
    // getPlacementStatus();
    getFinYearIntake();
  }, [selectedIntake]);

  useEffect(() => {
    const selectedKey = parseInt(params?.status);

    if (!selectedKey) {
      navigate(`/candidate/status/${submittedStatus.toString()}`);
      return;
    }

    if (location.state) {
      const {
        currentPage: cPage,
        pageSize: pSize,
        selectedIntake: currentIntake,
      } = location.state;

      const keepCurrentPage = pageSize === pSize;
      const first = keepCurrentPage ? (cPage - 1) * pageSize : (cPage - 1) * pSize;

      setPageSize(pSize);
      setCurrentPage(cPage);
      setSelectedIntake(currentIntake);
      getCandidatesByStatus({
        max: pSize,
        first: first,
        statusKey: selectedKey,
        intake: currentIntake,
      });
    } else {
      setSelectedIntake(selectedIntake);
      getCandidatesByStatus({
        max: pageSize,
        first: firstRecord,
        statusKey: selectedKey,
        intake: selectedIntake,
      });
    }
  }, [statuses.length > 0, finYearIntakes.length > 0]);

  const handleInputChange = (e) => {
    const { value: searchValue } = e.target;
    setSearchText(searchValue);
    // globalSearch(searchValue);
    setIsNotSearchable(searchValue.length === 0);

    if (searchValue === "" && isSearchedResults) {
      const selectedKey = parseInt(params?.status);

      setFirstRecord(0);
      setCurrentPage(1);
      getCandidatesByStatus({
        searchValue: searchValue,
        clearSearch: false,
        first: 0,
        statusKey: selectedKey,
        intake: selectedIntake,
      });
    }
  };

  const getFinYearIntake = async () => {
    try {
      const response = await service.getNYSIntake({ first: 0, max: 100 });
      if (response && response.status == 200) {
        const { results } = response.data;
        const sortData = results?.sort((a, b) => b.value - a.value);
        const newData = sortData.map((item) => ({
          key: item.id.toString(),
          label: item.value,
        }));

        setFinYearIntake(newData);

        if (!selectedIntake && newData.length > 0) {
          setSelectedIntake(newData[0].key);
        }

        // const defaultFinYear = newData?.slice(0, 1)[0];
      } else if (response && response.status == 204) {
        openNotification(
          "Candidates",
          "No NYS applications were found",
          Notify.WARNING
        );
      }
    } catch (error) {
      recordError(error);
    }
  };

  // const getPlacementStatus = async () => {
  //   try {
  //     const response = await workflowService.getPlacementStatus();
  //     var statusData = [];
  //     if (response && response.status == 200) {
  //       const { data } = response;

  //       data.sort((a, b) => a.key - b.key);
  //       statusData = data.map((status) => ({
  //         key: status.key.toString(),
  //         label: status.value,
  //         children: "",
  //       }));

  //       const defaultStatus = statusData.find((c) => c.label === "Submitted");
  //       // submittedStatus = defaultStatus.key;
  //       // setActiveKey(defaultStatus.key);
  //       console.log(
  //         "DATA OUT",
  //         statusData.find((c) => c.label === "Submitted")
  //       );

  //       setStatuses(statusData);
  //     } else {
  //       setSelectedStatus([]);
  //     }
  //   } catch (error) {
  //     recordError(error);
  //   }
  // };

  const getCandidatesByStatus = async ({
    statusKey = 0,
    first = firstRecord,
    max = pageSize,
    searchValue = searchText,
    clearSearch = true,
    intake = 0,
  }) => {
    if (clearSearch) {
      setSearchText("");
      searchValue = null;
    }
    setTotal(0);

    var actualStatusKey = statusKey ?? selectedStatus;
    var actualIntake = intake ?? selectedIntake;
    setSelectedStatus(actualStatusKey);
    setActiveKey(actualStatusKey.toString());

    try {
      //? Wait until statuses are populated before getting results
      //? This ensures that we are able to filter incorrect statuses

      if (statuses.length === 0) return;

      if (actualStatusKey || actualStatusKey > 0) {
        const statusExists = statuses?.filter((c) => c.key == actualStatusKey);

        if (statusExists.length == 0) {
          // default it to submitted status instead of the invalid value entered by the user
          actualStatusKey = submittedStatus;
        }
      } else if (
        actualStatusKey < 1 ||
        actualStatusKey == null ||
        actualStatusKey == undefined ||
        isNaN(actualStatusKey)
      ) {
        actualStatusKey = submittedStatus;
      }

      setLoading(true);
      const response = await service.getCandidatesByStatus({
        statusKey: actualStatusKey,
        first: first,
        max: max,
        text: searchValue,
        intake: actualIntake,
      });

      if (response && response.status === 200) {
        var candidateData = [];
        const { data } = response;
        setTotal(data?.total);
        candidateData = data.results?.map((element) => ({
          key: element.candidateId.toString() + "x" + element.userId,
          candidateId: element.candidateId,
          fullname: element.fullname,
          race: element.race,
          gender: element.gender,
          disability: element.disability,
          disabilityField: element.disability ? "Yes" : "No",
          fieldOfStudy: element.fieldOfStudy,
          placementStatus: element.placementStatus,
          preferredProvince: element.preferredProvince,
          selectedStatus: statuses?.find((c) => c.key == actualStatusKey),
          // currentPage: currentPage,
          // pageSize: pageSize,
          applicationDate: moment(element.applicationDate).format("DD-MM-YYYY"),
        }));
        candidateData.sort((a, b) => a.candidateId - b.candidateId);

        setCandidates(candidateData);
        setFilteredData(candidateData);

        setLoading(false);
      } else if (response && response.status === 204) {
        setLoading(false);
        setCandidates([]);
        setFilteredData([]);
      } else {
        setCandidates([]);
        setFilteredData([]);
      }
    } catch (error) {
      recordError(error);
    }
    setIsSearchedResults(
      searchValue !== null && searchValue !== undefined && searchValue !== ""
    );
    // navigate(`/candidate/status/${actualStatusKey.toString()}`);
  };

  const globalSearch = (searchValue) => {
    if (searchValue === "") {
      setFilteredData(candidates);
      return;
    }

    const reg = new RegExp(
      searchValue.replace(/[.*+\-?^${}()|[\]\\]/g, "\\$&"),
      "gi"
    );

    var _filteredData = map(candidates, (record) => {
      const fullnameMatch = get(record, "fullname").match(reg);
      const raceMatch = get(record, "race").match(reg);
      const genderMatch = get(record, "gender").match(reg);
      const fieldOfStudyMatch = get(record, "fieldOfStudy").match(reg);
      const provinceMatch = get(record, "preferredProvince").match(reg);

      if (
        !fullnameMatch &&
        !raceMatch &&
        !genderMatch &&
        !fieldOfStudyMatch &&
        !provinceMatch
      ) {
        return null;
      }
      return record;
    }).filter((record) => record);

    setFilteredData(_filteredData);
  };

  const onStatusChange = (key) => {
    const keyValue = statuses.find((status) => status.key === key);
    setSelectedStatus(key);
    setActiveKey(key.toString());
    getCandidatesByStatus({ statusKey: key, intake: selectedIntake });
    setFirstRecord(0);
    setCurrentPage(1);
    setSearchText("");

    navigate(`/candidate/status/${key}`);

    if (
      keyValue.label === "Submitted" ||
      keyValue.label === "In Progress" ||
      keyValue.label === "In Review" ||
      keyValue.label === "Placed"
    ) {
      setTableAction("Manage");
    } else {
      setTableAction("View");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "fullname",
      key: "1",
      // defaultSortOrder: "ascend",
      sorter: (a, b) => a.fullname.localeCompare(b.fullname),
    },
    {
      title: "Race",
      dataIndex: "race",
      key: "2",
      sorter: (a, b) => a.race.localeCompare(b.race),
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "3",
      sorter: (a, b) => a.gender.localeCompare(b.gender),
    },
    {
      title: "Disability",
      dataIndex: "disability",
      key: "4",
      render: (value) =>
        value == true ? (
          <CheckOutlined
            style={{
              fontSize: "18px",
              color: "#04a596",
              fontWeight: "bold",
            }}
          />
        ) : (
          <CloseOutlined
            style={{
              fontSize: "18px",
              color: "#a21b1b",
              fontWeight: "bold",
            }}
          />
        ),
      sorter: (a, b) => a.disabilityField.localeCompare(b.disabilityField),
    },
    {
      title: "Field of study",
      dataIndex: "fieldOfStudy",
      key: "5",
      sorter: (a, b) => a.fieldOfStudy.localeCompare(b.fieldOfStudy),
    },
    {
      title: "Preferred placement province",
      dataIndex: "preferredProvince",
      key: "6",
      sorter: (a, b) => a.preferredProvince.localeCompare(b.preferredProvince),
    },
    {
      title: "Application Date",
      dataIndex: "applicationDate",
      key: "6a",
      // sorter: (a, b) => a.preferredProvince.localeCompare(b.preferredProvince),
    },
    {
      title: tableAction,
      dataIndex: "manage",
      key: "7",
      render: (_, record, index) => {
        const currentRecord = {
          ...record,
          index: index + 1,
          currentPage: currentPage,
          pageSize: pageSize,
          selectedIntake: selectedIntake,
        };
        return (
          <>
            <Link
              to={`/candidate/${record.key}`}
              state={{ from: location.pathname, candidateslist: currentRecord }}
            >
              <Button type="primary" icon={<FundViewOutlined />}></Button>
            </Link>
          </>
        );
      },
    },
  ];

  const onRowChange = (row) => {
    setPageSize(row.pageSize);
  };

  const onChangeFinYear = (intakeValue) => {
    // const selected = finYearIntakes.find((c) => c.key === intakeValue);
    setSelectedIntake(intakeValue);

    const selectedKey = parseInt(params?.status);
    getCandidatesByStatus({ statusKey: selectedKey, intake: intakeValue });
  };

  const onPageChange = (page, size) => {
    const keepCurrentPage = pageSize === size;
    const first = keepCurrentPage ? (page - 1) * pageSize : 0;
    const newPage = keepCurrentPage ? page : 1;

    setFirstRecord(first);
    setPageSize(size);
    setCurrentPage(newPage);

    const selectedKey = parseInt(params?.status);
    setSelectedStatus(selectedKey);
    getCandidatesByStatus({
      first: first,
      max: size,
      clearSearch: false,
      statusKey: selectedKey,
      intake: selectedIntake,
    });
  };

  const onShowSizeChange = (page, size) => {
    const keepCurrentPage = pageSize === size;
    const first = keepCurrentPage ? (page - 1) * pageSize : 0;
    const newPage = keepCurrentPage ? page : 1;

    setPageSize(size);
    setCurrentPage(newPage);
  };

  const handleSearchRequest = async () => {
    // Do nothing if searchText is empty on hitting enter
    if (searchText.length > 0) {
      const selectedKey = parseInt(params?.status);

      setFirstRecord(0);
      getCandidatesByStatus({
        first: 0,
        clearSearch: false,
        statusKey: selectedKey,
        intake: selectedIntake,
      });
      setIsSearchedResults(true);
    }
  };

  return (
    <>
      <div className="content">
        <Col xs={24}>
          <h1 className="center-align">NYS Candidates List</h1>
        </Col>
        <div class="space"></div>
        {/* <div>List of NYS Candidate</div> */}
        <Row>
          <Col xs={15} md={8}>
            <Input
              placeholder="Search Candidate"
              type="text"
              onChange={handleInputChange}
              onPressEnter={handleSearchRequest}
              onPaste={handleInputChange}
              value={searchText}
              allowClear
              size="large"
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={8} md={8}>
            <Button
              disabled={isNotSearchable}
              key="Search"
              value="Search"
              type="primary"
              onClick={handleSearchRequest}
              style={{ marginLeft: 10 }}
            >
              Search
            </Button>
          </Col>
        </Row>
        <Divider />
        <Row>
          <Col xs={24} md={4}>
            Financial Year (intake) :&nbsp;
          </Col>
          <Col xs={24} md={4}>
            <Select
              // style={{ width: "65%" }}
              placeholder="Please select financial year"
              onChange={onChangeFinYear}
              loading={!finYearIntakes.length}
              value={selectedIntake}
              size="large"
            >
              {finYearIntakes?.map((year) => {
                return (
                  <Option key={year.key} value={year.key}>
                    {year.label}
                  </Option>
                );
              })}
            </Select>
          </Col>
        </Row>
        <div className="space"></div>

        <Tabs
          className="nys-status-tabs"
          type="card"
          onChange={onStatusChange}
          items={statuses}
          activeKey={activeKey}
          defaultActiveKey={submittedStatus}
          key="7"
        />
        {loading ? (
          <Skeleton active avatar />
        ) : (
          <Table
            dataSource={filteredData}
            rowKey={(record) => record.candidateId}
            columns={columns}
            loading={loading}
            onChange={onRowChange}
            bordered
            size="middle"
            pagination={{
              defaultCurrent: 1,
              current: currentPage,
              defaultPageSize: pageSize,
              total: total,
              pageSize: pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              onChange: onPageChange,
              onShowSizeChange: onShowSizeChange,
              pageSizeOptions: [2, 5, 10, 20, 50, 100],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} ${
                  total > 1 ? "Candidates" : "Candidate"
                }`,
            }}
          />
        )}
      </div>
    </>
  );
};

export default CandidatesList;
